import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, fmtDateSlash, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, FileSpreadsheet, FileText, FileSignature, ArrowLeft, Download, ArrowUp, ArrowDown, ArrowUpDown, ArrowDownCircle, ArrowUpCircle, MapPin, Receipt, Boxes } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTurkishFont } from "@/lib/pdfFont";
import ReconciliationModal from "@/pages/ReconciliationModal";
import SettlementModal from "@/components/SettlementModal";
import PriceQuotesModal from "@/components/PriceQuotesModal";
import ContactStocksModal from "@/components/ContactStocksModal";
import { TR_PROVINCES } from "@/lib/tr-provinces";

async function loadLogoDataUrl() {
  try {
    // Küçük JPG (~96KB) kullan — PDF boyutunu düşürmek için
    const res = await fetch("/plasper-logo.jpg");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

const TYPES = { customer: "Müşteri", supplier: "Tedarikçi", both: "Her İkisi" };

// Cari türüne göre renk paleti — müşteri yeşil (para gelir), tedarikçi kırmızı (para gider), karma sarı.
const TYPE_STYLES = {
  customer: {
    badge: "bg-emerald-50 text-emerald-800 border-emerald-400",
    row: "border-l-4 border-l-emerald-500",
    dot: "bg-emerald-500",
    activeBtn: "bg-emerald-600 text-white border-emerald-700",
    idleBtn: "bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50",
  },
  supplier: {
    badge: "bg-rose-50 text-rose-800 border-rose-400",
    row: "border-l-4 border-l-rose-500",
    dot: "bg-rose-500",
    activeBtn: "bg-rose-600 text-white border-rose-700",
    idleBtn: "bg-white text-rose-800 border-rose-300 hover:bg-rose-50",
  },
  both: {
    badge: "bg-amber-50 text-amber-800 border-amber-400",
    row: "border-l-4 border-l-amber-500",
    dot: "bg-amber-500",
    activeBtn: "bg-amber-500 text-white border-amber-600",
    idleBtn: "bg-white text-amber-800 border-amber-300 hover:bg-amber-50",
  },
  neutral: {
    activeBtn: "bg-black text-white border-black",
    idleBtn: "bg-white text-black border-black/30 hover:bg-neutral-50",
  },
};
const typeStyle = (t) => TYPE_STYLES[t] || { badge: "border-black/10", row: "", dot: "bg-neutral-400" };

function TypeFilterBtn({ active, onClick, label, tone }) {
  const st = TYPE_STYLES[tone] || TYPE_STYLES.neutral;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`type-filter-${tone}`}
      className={`border px-3 py-1.5 text-xs font-medium transition-colors ${active ? st.activeBtn : st.idleBtn}`}
    >
      {label}
    </button>
  );
}

export default function Contacts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const provinceFilter = searchParams.get("province") || "";
  const typeFilter = searchParams.get("type") || ""; // customer | supplier | ""

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statementFor, setStatementFor] = useState(null);
  const [reconcileFor, setReconcileFor] = useState(null);
  const [settleFor, setSettleFor] = useState(null); // { contact, direction }
  const [quoteFor, setQuoteFor] = useState(null); // contact — Fiyat Teklifleri modal
  const [stocksFor, setStocksFor] = useState(null); // contact — Bağlı Stoklar modal
  const [quotesSummary, setQuotesSummary] = useState({}); // cid -> {last_date, last_total, count, last_id}
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ field: "name", dir: "asc" });

  const load = useCallback(async () => {
    const [{ data: cs }, { data: sm }] = await Promise.all([
      api.get("/contacts"),
      api.get("/price-quotes/summary").catch(() => ({ data: {} })),
    ]);
    setItems(cs);
    setQuotesSummary(sm || {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const clearGeoFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("province");
    next.delete("type");
    setSearchParams(next, { replace: true });
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/contacts/${id}`); toast.success("Silindi"); load();
  };

  const toggleErpSync = async (contact) => {
    const next = !contact.erp_synced;
    // Optimistic update: işaretlerken şimdiki tarihi yaz; kaldırırken önceki tarihi KORU.
    const nextDate = next ? new Date().toISOString() : contact.erp_synced_at;
    setItems((prev) => prev.map((c) => (c.id === contact.id
      ? { ...c, erp_synced: next, erp_synced_at: nextDate }
      : c)));
    try {
      const { data } = await api.post(`/contacts/${contact.id}/erp-sync`, { synced: next });
      // Server-authoritative refresh (özellikle timestamp)
      setItems((prev) => prev.map((c) => (c.id === contact.id
        ? { ...c, erp_synced: data.erp_synced, erp_synced_at: data.erp_synced_at }
        : c)));
    } catch (e) {
      // rollback on error
      setItems((prev) => prev.map((c) => (c.id === contact.id
        ? { ...c, erp_synced: contact.erp_synced, erp_synced_at: contact.erp_synced_at }
        : c)));
      toast.error("Güncellenemedi: " + (e?.response?.data?.detail || e?.message || ""));
    }
  };

  const toggleAccountingSent = async (contact) => {
    const next = !contact.accounting_sent;
    const nextDate = next ? new Date().toISOString() : contact.accounting_sent_at;
    setItems((prev) => prev.map((c) => (c.id === contact.id
      ? { ...c, accounting_sent: next, accounting_sent_at: nextDate }
      : c)));
    try {
      const { data } = await api.post(`/contacts/${contact.id}/accounting-sent`, { sent: next });
      setItems((prev) => prev.map((c) => (c.id === contact.id
        ? { ...c, accounting_sent: data.accounting_sent, accounting_sent_at: data.accounting_sent_at }
        : c)));
    } catch (e) {
      setItems((prev) => prev.map((c) => (c.id === contact.id
        ? { ...c, accounting_sent: contact.accounting_sent, accounting_sent_at: contact.accounting_sent_at }
        : c)));
      toast.error("Güncellenemedi: " + (e?.response?.data?.detail || e?.message || ""));
    }
  };

  const toggleSort = (field) => {
    setSort((prev) => {
      if (prev.field !== field) {
        const initDir = field === "balance" ? "desc" : "asc";
        return { field, dir: initDir };
      }
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const filtered = items.filter((c) => {
    if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (provinceFilter && (c.province || "").toLocaleLowerCase("tr") !== provinceFilter.toLocaleLowerCase("tr")) return false;
    if (typeFilter) {
      // customer filter matches "customer" or "both"; supplier matches "supplier" or "both"
      const t = (c.type || "").toLowerCase();
      if (typeFilter === "customer" && !(t === "customer" || t === "both")) return false;
      if (typeFilter === "supplier" && !(t === "supplier" || t === "both")) return false;
    }
    return true;
  });
  const sorted = React.useMemo(() => {
    const getKey = (c) => {
      switch (sort.field) {
        case "type": return (TYPES[c.type] || "").toLocaleLowerCase("tr");
        case "contact": return (c.email || c.phone || "").toLocaleLowerCase("tr");
        case "tax": return (c.tax_id || "").toString();
        case "balance": return Number(c.balance) || 0;
        case "erp": return c.erp_synced ? 1 : 0;
        case "acc": return c.accounting_sent ? 1 : 0;
        default: return (c.name || "").toLocaleLowerCase("tr");
      }
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = getKey(a); const bv = getKey(b);
      const aEmpty = av === "" || av === null || av === undefined;
      const bEmpty = bv === "" || bv === null || bv === undefined;
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return 0;
      let cmp;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), "tr");
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sort]);

  const totalPos = items.reduce((s, c) => s + Math.max(c.balance, 0), 0);
  const totalNeg = items.reduce((s, c) => s + Math.min(c.balance, 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Müşteri & Tedarikçi</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Cari Hesaplar</h1>
        </div>
        <button data-testid="contact-add-button" onClick={() => { setEditing(null); setOpen(true); }}
                className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2">
          <Plus size={16} /> Yeni Cari
        </button>
      </header>

      {provinceFilter && (
        <div
          data-testid="contact-geo-filter-chip"
          className="flex items-center gap-2 bg-amber-50 border border-amber-300 px-3 py-2 text-sm"
        >
          <MapPin size={14} className="text-amber-700" />
          <span>
            Filtre: <b>{provinceFilter}</b>
            {typeFilter === "customer" && <span className="ml-1 text-emerald-700">· Müşteri</span>}
            {typeFilter === "supplier" && <span className="ml-1 text-rose-600">· Tedarikçi</span>}
            <span className="ml-2 text-neutral-500 text-xs">({filtered.length} sonuç)</span>
          </span>
          <button
            data-testid="contact-geo-filter-clear"
            onClick={clearGeoFilter}
            className="ml-auto text-xs uppercase tracking-widest hover:text-amber-800 flex items-center gap-1"
          >
            <X size={12} /> Filtreyi Temizle
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="contact-type-filter">
        <span className="label-caps text-xs mr-1">Tür Filtresi:</span>
        <TypeFilterBtn
          active={typeFilter === ""}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("type");
            setSearchParams(next, { replace: true });
          }}
          label={`Tümü (${items.length})`}
          tone="neutral"
        />
        <TypeFilterBtn
          active={typeFilter === "customer"}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.set("type", "customer");
            setSearchParams(next, { replace: true });
          }}
          label={`Müşteri (${items.filter(c => c.type === "customer" || c.type === "both").length})`}
          tone="customer"
        />
        <TypeFilterBtn
          active={typeFilter === "supplier"}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.set("type", "supplier");
            setSearchParams(next, { replace: true });
          }}
          label={`Tedarikçi (${items.filter(c => c.type === "supplier" || c.type === "both").length})`}
          tone="supplier"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="brutal-card p-5">
          <div className="label-caps">Borç Bakiyeli Cariler</div>
          <div className="font-mono-num text-2xl mt-2 text-emerald-700">{fmtTRY(totalPos)}</div>
        </div>
        <div className="brutal-card p-5">
          <div className="label-caps">Alacak Bakiyeli Cariler</div>
          <div className="font-mono-num text-2xl mt-2 text-rose-600">{fmtTRY(Math.abs(totalNeg))}</div>
        </div>
        <div className="brutal-card p-5">
          <div className="label-caps">Cari Sayısı</div>
          <div className="font-mono-num text-2xl mt-2">{items.length}</div>
        </div>
      </div>

      <input data-testid="contact-search" placeholder="Ara…" value={q} onChange={(e) => setQ(e.target.value)}
             className="w-full max-w-sm border border-black/20 px-3 py-2 outline-none focus:ring-2 focus:ring-black" />

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="contacts-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <SortTh field="name" label="Ad" sort={sort} onToggle={toggleSort} tid="sort-contact-name" />
              <SortTh field="type" label="Tür" sort={sort} onToggle={toggleSort} tid="sort-contact-type" />
              <SortTh field="contact" label="İletişim" sort={sort} onToggle={toggleSort} tid="sort-contact-contact" />
              <SortTh field="tax" label="VKN/TCKN" sort={sort} onToggle={toggleSort} tid="sort-contact-tax" />
              <SortTh field="balance" label="Bakiye" sort={sort} onToggle={toggleSort} align="right" tid="sort-contact-balance" />
              <SortTh field="erp" label="ERP Mutabık" sort={sort} onToggle={toggleSort} align="center" tid="sort-contact-erp" />
              <SortTh field="acc" label="Muhasebeye İletildi" sort={sort} onToggle={toggleSort} align="center" tid="sort-contact-acc" />
              <th className="text-left px-4 py-3 label-caps whitespace-nowrap">Son Teklif</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-neutral-500">Kayıt yok.</td></tr>
            )}
            {sorted.map((c) => {
              const st = typeStyle(c.type);
              return (
              <tr key={c.id} className={`border-b border-black/5 hover:bg-neutral-50 ${st.row}`}>
                <td className="px-4 py-3 font-medium">
                  <button
                    type="button"
                    data-testid={`contact-open-${c.id}`}
                    onClick={() => setStatementFor(c)}
                    className="text-left underline decoration-black/20 underline-offset-4 hover:decoration-black hover:decoration-2 transition-all"
                    title="Cari hareketlerini görüntüle"
                  >
                    {c.name}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-xs font-medium ${st.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    {TYPES[c.type]}
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-600">{c.email || c.phone || "—"}</td>
                <td className="px-4 py-3 font-mono-num text-neutral-600">{c.tax_id || "—"}</td>
                <td className={`px-4 py-3 text-right font-mono-num font-semibold ${c.balance > 0 ? "text-emerald-700" : c.balance < 0 ? "text-rose-600" : ""}`}>
                  {fmtTRY(c.balance)}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-2">
                    <label
                      className="inline-flex items-center justify-center cursor-pointer select-none"
                      title={c.erp_synced
                        ? `ERP ile mutabık${c.erp_synced_at ? ` · ${fmtDate(c.erp_synced_at.slice(0,10))}` : ""}`
                        : c.erp_synced_at
                          ? `Şu an mutabık değil · Son mutabakat: ${fmtDate(c.erp_synced_at.slice(0,10))}`
                          : "Hiç mutabakat yapılmadı"}
                    >
                      <input
                        type="checkbox"
                        data-testid={`contact-erp-sync-${c.id}`}
                        checked={!!c.erp_synced}
                        onChange={() => toggleErpSync(c)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 accent-emerald-700 cursor-pointer"
                      />
                    </label>
                    {c.erp_synced_at ? (
                      <span
                        data-testid={`contact-erp-sync-date-${c.id}`}
                        className={`font-mono-num text-[11px] whitespace-nowrap ${c.erp_synced ? "text-emerald-700" : "text-neutral-500"}`}
                        title={c.erp_synced ? "Bu tarihte mutabık işaretlendi" : "Bir önceki mutabakat tarihi"}
                      >
                        {fmtDate(c.erp_synced_at.slice(0, 10))}
                      </span>
                    ) : (
                      <span className="text-neutral-300 text-[11px]">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-2">
                    <label
                      className="inline-flex items-center justify-center cursor-pointer select-none"
                      title={c.accounting_sent
                        ? `Muhasebeye iletildi${c.accounting_sent_at ? ` · ${fmtDate(c.accounting_sent_at.slice(0,10))}` : ""}`
                        : c.accounting_sent_at
                          ? `Şu an iletilmedi olarak işaretli · Son iletim: ${fmtDate(c.accounting_sent_at.slice(0,10))}`
                          : "Henüz muhasebeye iletilmedi"}
                    >
                      <input
                        type="checkbox"
                        data-testid={`contact-accounting-sent-${c.id}`}
                        checked={!!c.accounting_sent}
                        onChange={() => toggleAccountingSent(c)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 accent-emerald-700 cursor-pointer"
                      />
                    </label>
                    {c.accounting_sent_at ? (
                      <span
                        data-testid={`contact-accounting-sent-date-${c.id}`}
                        className={`font-mono-num text-[11px] whitespace-nowrap ${c.accounting_sent ? "text-emerald-700" : "text-neutral-500"}`}
                        title={c.accounting_sent ? "Bu tarihte muhasebeye iletildi olarak işaretlendi" : "Bir önceki iletim tarihi"}
                      >
                        {fmtDate(c.accounting_sent_at.slice(0, 10))}
                      </span>
                    ) : (
                      <span className="text-neutral-300 text-[11px]">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-xs" data-testid={`contact-last-quote-${c.id}`}>
                  {quotesSummary[c.id] ? (
                    <button
                      type="button"
                      onClick={() => setQuoteFor(c)}
                      className="text-left hover:bg-neutral-100 px-1 -mx-1"
                      title={`${quotesSummary[c.id].count} teklif · Tıklayarak aç`}
                    >
                      <div className="font-mono-num text-neutral-700">{fmtDate(quotesSummary[c.id].last_date)}</div>
                      <div className="font-mono-num text-emerald-700 font-semibold">
                        {fmtTRY(quotesSummary[c.id].last_total)}
                        <span className="text-[10px] text-neutral-500 ml-1">({quotesSummary[c.id].count})</span>
                      </div>
                    </button>
                  ) : (
                    <span className="text-neutral-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`contact-collect-${c.id}`}
                    onClick={() => setSettleFor({ contact: c, direction: "collection" })}
                    className="p-1.5 hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
                    title="Tahsilat"
                  >
                    <ArrowDownCircle size={14} className="text-emerald-700" />
                  </button>
                  <button
                    data-testid={`contact-pay-${c.id}`}
                    onClick={() => setSettleFor({ contact: c, direction: "payment" })}
                    className="p-1.5 hover:bg-rose-50 border border-transparent hover:border-rose-200"
                    title="Ödeme"
                  >
                    <ArrowUpCircle size={14} className="text-rose-600" />
                  </button>
                  <button
                    data-testid={`contact-statement-${c.id}`}
                    onClick={() => setStatementFor(c)}
                    className="p-1.5 hover:bg-neutral-100 border border-transparent hover:border-black/10"
                    title="Ekstre"
                  >
                    <FileText size={14} />
                  </button>
                  <button
                    data-testid={`contact-reconcile-${c.id}`}
                    onClick={() => setReconcileFor(c)}
                    className="p-1.5 hover:bg-neutral-100 border border-transparent hover:border-black/10"
                    title="Mutabakat"
                  >
                    <FileSignature size={14} />
                  </button>
                  <button
                    data-testid={`contact-quotes-${c.id}`}
                    onClick={() => setQuoteFor(c)}
                    className="p-1.5 hover:bg-neutral-100 border border-transparent hover:border-black/10"
                    title="Fiyat Teklifleri"
                  >
                    <Receipt size={14} />
                  </button>
                  <button
                    data-testid={`contact-stocks-${c.id}`}
                    onClick={() => setStocksFor(c)}
                    className="p-1.5 hover:bg-neutral-100 border border-transparent hover:border-black/10"
                    title="Bağlı Stoklar"
                  >
                    <Boxes size={14} />
                  </button>
                  <button onClick={() => { setEditing(c); setOpen(true); }} className="p-1.5 hover:bg-neutral-100"><Edit3 size={14} /></button>
                  <button data-testid={`contact-delete-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 hover:bg-rose-50"><Trash2 size={14} className="text-rose-600" /></button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {open && <ContactModal editing={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {statementFor && (
        <StatementModal
          contact={statementFor}
          onClose={() => setStatementFor(null)}
        />
      )}
      {reconcileFor && (
        <ReconciliationModal
          contact={reconcileFor}
          onClose={() => setReconcileFor(null)}
        />
      )}
      {settleFor && (
        <SettlementModal
          contact={settleFor.contact}
          direction={settleFor.direction}
          onClose={() => setSettleFor(null)}
          onDone={() => { setSettleFor(null); load(); }}
        />
      )}
      {quoteFor && (
        <PriceQuotesModal
          contact={quoteFor}
          onClose={() => setQuoteFor(null)}
          onChanged={load}
        />
      )}
      {stocksFor && (
        <ContactStocksModal
          contact={stocksFor}
          onClose={() => setStocksFor(null)}
        />
      )}
    </div>
  );
}

function ContactModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || { name: "", type: "customer", email: "", phone: "", tax_id: "", address: "", province: "", district: "", payment_term_days: "", notes: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        payment_term_days:
          form.payment_term_days === "" || form.payment_term_days === null || form.payment_term_days === undefined
            ? null
            : Number(form.payment_term_days),
        province: form.province || null,
        district: form.district || null,
      };
      if (editing) await api.put(`/contacts/${editing.id}`, payload);
      else await api.post("/contacts", payload);
      toast.success("Kaydedildi"); onSaved();
    } catch { toast.error("Hata"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-black w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Cariyi Düzenle" : "Yeni Cari"}</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <F label="Ad"><input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></F>
          <F label="Tür">
            <select data-testid="contact-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input bg-white">
              {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="E-posta"><input data-testid="contact-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" /></F>
            <F label="Telefon"><input data-testid="contact-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" /></F>
          </div>
          <F label="VKN / TCKN"><input data-testid="contact-tax" value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="input font-mono-num" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="İl">
              <select
                data-testid="contact-province"
                value={form.province || ""}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                className="input bg-white"
              >
                <option value="">— Seçim yok —</option>
                {TR_PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </F>
            <F label="İlçe">
              <input
                data-testid="contact-district"
                value={form.district || ""}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
                placeholder="Örn: Kadıköy"
                className="input"
              />
            </F>
          </div>
          <F label="Anlaşma Vadesi (Gün)">
            <input
              data-testid="contact-payment-term"
              type="number"
              min="0"
              max="365"
              placeholder="Örn: 30, 60, 90 — Boş bırakılırsa peşin"
              value={form.payment_term_days ?? ""}
              onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })}
              className="input font-mono-num"
            />
            <div className="text-[10px] text-neutral-500 mt-1">Fatura tarihi + belirtilen gün = otomatik vade tarihi. CSV içe aktarmada uygulanır.</div>
          </F>
          <F label="Adres"><textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="input" /></F>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="contact-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}
function F({ label, children }) { return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>; }

function SortTh({ field, label, sort, onToggle, align, tid }) {
  const isActive = sort.field === field;
  const Icon = !isActive ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        data-testid={tid}
        onClick={() => onToggle(field)}
        className={`label-caps inline-flex items-center gap-1 select-none ${
          isActive ? "text-black" : "text-neutral-500 hover:text-black"
        } ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        <span>{label}</span>
        <Icon size={11} strokeWidth={2.2} className={isActive ? "opacity-100" : "opacity-50"} />
      </button>
    </th>
  );
}

// ================= Statement (Cari Ekstre) =================

function StatementModal({ contact, onClose }) {
  const navigate = useNavigate();
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().slice(0, 10);
  const [start, setStart] = useState(first);
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/contacts/${contact.id}/statement?start=${start}&end=${end}`,
      );
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [contact.id, start, end]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // Ekstre satırları
    const rows = [];
    rows.push({
      Tarih: "",
      Açıklama: "DÖNEM BAŞI BAKİYE",
      Kategori: "",
      Borç: "",
      Alacak: "",
      Bakiye: data.opening_balance,
    });
    // Excel: Cari kartında Borç/Alacak (T-hesap)
    data.transactions.forEach((t) => {
      const prefix = t.source === "invoice" ? "[FATURA] " : (t.source === "cheque" ? "[ÇEK] " : "");
      rows.push({
        Tarih: fmtDateSlash(t.date),
        Açıklama: prefix + (t.description || "—"),
        Kategori: t.category || "",
        Borç: t.income || "",
        Alacak: t.expense || "",
        Bakiye: t.balance,
      });
    });
    rows.push({
      Tarih: "",
      Açıklama: "DÖNEM SONU BAKİYE",
      Kategori: "",
      Borç: data.total_income,
      Alacak: data.total_expense,
      Bakiye: data.closing_balance,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 12 }, { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 },
    ];

    // Başlık meta sheet
    const meta = [
      ["PLASPER FİNANS - Cari Ekstre"],
      [""],
      ["Cari:", contact.name],
      ["Tür:", TYPES[contact.type] || ""],
      ["VKN/TCKN:", contact.tax_id || ""],
      ["E-posta:", contact.email || ""],
      ["Telefon:", contact.phone || ""],
      [""],
      ["Dönem:", `${fmtDateSlash(start)} — ${fmtDateSlash(end)}`],
      ["Dönem Başı Bakiye:", data.opening_balance],
      ["Toplam Borç:", data.total_income],
      ["Toplam Alacak:", data.total_expense],
      ["Dönem Net:", data.period_net],
      ["Dönem Sonu Bakiye:", data.closing_balance],
      [""],
      ["ORTALAMA VADE (Açık Faturalar, Ref: " + fmtDateSlash(data.reference_date || end) + ")"],
      ["Borç Ort. Vade (gün):", data.avg_maturity_receivable_days ?? "—"],
      ["Borç Ort. Vade Tarihi:", fmtDateSlash(data.avg_maturity_receivable_date) || "—"],
      ["Borç Açık Satış Fatura Sayısı / Tutarı:",
        `${data.avg_maturity_receivable_count || 0} adet / ${data.avg_maturity_receivable_amount || 0}`],
      ["Alacak Ort. Vade (gün):", data.avg_maturity_payable_days ?? "—"],
      ["Alacak Ort. Vade Tarihi:", fmtDateSlash(data.avg_maturity_payable_date) || "—"],
      ["Alacak Açık Alış Fatura Sayısı / Tutarı:",
        `${data.avg_maturity_payable_count || 0} adet / ${data.avg_maturity_payable_amount || 0}`],
    ];
    const wsMeta = XLSX.utils.aoa_to_sheet(meta);
    wsMeta["!cols"] = [{ wch: 24 }, { wch: 40 }];

    XLSX.utils.book_append_sheet(wb, wsMeta, "Özet");
    XLSX.utils.book_append_sheet(wb, ws, "Ekstre");

    const safeName = contact.name.replace(/[^a-zA-Z0-9-_ğüşıöçİĞÜŞÖÇ]/g, "_");
    XLSX.writeFile(wb, `ekstre_${safeName}_${start}_${end}.xlsx`);
    toast.success("Excel indirildi");
  };

  const exportPDF = async () => {
    if (!data) return;
    const doc = new jsPDF({ compress: true });
    await registerTurkishFont(doc);
    const logo = await loadLogoDataUrl();
    if (logo) {
      try { doc.addImage(logo, "JPEG", 14, 12, 45, 15, undefined, "FAST"); } catch {}
    }
    // Header
    doc.setFontSize(16); doc.text("CARİ EKSTRE", 65, 20);
    doc.setFontSize(9); doc.setTextColor(90);
    doc.text("Plasper Finans", 65, 26);
    doc.setTextColor(0);

    // Contact info block
    doc.setFontSize(11); doc.setFont(undefined, "bold");
    doc.text(contact.name, 14, 46);
    doc.setFont(undefined, "normal"); doc.setFontSize(9);
    let y = 51;
    [
      TYPES[contact.type],
      contact.tax_id && `VKN/TCKN: ${contact.tax_id}`,
      contact.email, contact.phone,
    ].filter(Boolean).forEach((line) => {
      doc.text(String(line).slice(0, 80), 14, y); y += 4.5;
    });

    // Period line
    doc.setFontSize(10);
    doc.text(`Dönem: ${fmtDateSlash(start)} — ${fmtDateSlash(end)}`, 14, y + 4);

    // Summary table
    autoTable(doc, {
      startY: y + 10,
      head: [["Özet", "Tutar (₺)"]],
      body: [
        ["Dönem Başı Bakiye", fmtTRY(data.opening_balance)],
        ["Toplam Borç (+)", fmtTRY(data.total_income)],
        ["Toplam Alacak (−)", fmtTRY(data.total_expense)],
        [{ content: "Dönem Sonu Bakiye", styles: { fontStyle: "bold" } },
         { content: fmtTRY(data.closing_balance), styles: { fontStyle: "bold" } }],
      ],
      styles: { fontSize: 10, font: "Roboto" },
      headStyles: { fillColor: [10, 10, 10], font: "Roboto", fontStyle: "bold" },
      columnStyles: { 1: { halign: "right" } },
    });

    // Ortalama vade özeti (varsa)
    if (data.avg_maturity_receivable_days !== null || data.avg_maturity_payable_days !== null) {
      const avgBody = [];
      if (data.avg_maturity_receivable_days !== null) {
        avgBody.push([
          `Borç (${data.avg_maturity_receivable_count} açık satış faturası, ${fmtTRY(data.avg_maturity_receivable_amount)})`,
          `${Math.round(data.avg_maturity_receivable_days)} gün`,
          fmtDateSlash(data.avg_maturity_receivable_date) || "—",
        ]);
      }
      if (data.avg_maturity_payable_days !== null) {
        avgBody.push([
          `Alacak (${data.avg_maturity_payable_count} açık alış faturası, ${fmtTRY(data.avg_maturity_payable_amount)})`,
          `${Math.round(data.avg_maturity_payable_days)} gün`,
          fmtDateSlash(data.avg_maturity_payable_date) || "—",
        ]);
      }
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 4,
        head: [[`Ortalama Vade (Ref: ${fmtDateSlash(data.reference_date || end)})`, "Ort. Gün", "Ort. Vade Tarihi"]],
        body: avgBody,
        styles: { fontSize: 10, font: "Roboto" },
        headStyles: { fillColor: [10, 10, 10], font: "Roboto", fontStyle: "bold" },
        columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
      });
    }

    // Ledger rows
    const ledger = [
      [
        { content: "Dönem Başı Bakiye", colSpan: 4, styles: { fontStyle: "italic", fillColor: [245, 245, 245] } },
        { content: fmtTRY(data.opening_balance),
          styles: { halign: "right", fontStyle: "bold", fillColor: [245, 245, 245] } },
      ],
    ];
    data.transactions.forEach((t) => {
      ledger.push([
        fmtDateSlash(t.date),
        (t.description || "-") + (t.category ? `\n${t.category}` : ""),
        t.income ? fmtTRY(t.income) : "-",
        t.expense ? fmtTRY(t.expense) : "-",
        fmtTRY(t.balance),
      ]);
    });
    ledger.push([
      { content: "Dönem Sonu", colSpan: 2, styles: { fontStyle: "bold", fillColor: [230, 230, 230] } },
      { content: fmtTRY(data.total_income), styles: { halign: "right", fontStyle: "bold", fillColor: [230, 230, 230] } },
      { content: fmtTRY(data.total_expense), styles: { halign: "right", fontStyle: "bold", fillColor: [230, 230, 230] } },
      { content: fmtTRY(data.closing_balance), styles: { halign: "right", fontStyle: "bold", fillColor: [230, 230, 230] } },
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [["Tarih", "Açıklama", "Borç (+)", "Alacak (−)", "Bakiye"]],
      body: ledger,
      styles: { fontSize: 8, cellPadding: 2, font: "Roboto" },
      headStyles: { fillColor: [10, 10, 10], font: "Roboto", fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 22 },
        2: { halign: "right", cellWidth: 30 },
        3: { halign: "right", cellWidth: 30 },
        4: { halign: "right", cellWidth: 32 },
      },
      didDrawPage: () => {
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(
          `Plasper Finans · Cari Ekstre · ${contact.name} · Sayfa ${doc.internal.getNumberOfPages()}`,
          14, doc.internal.pageSize.getHeight() - 8,
        );
      },
    });

    const safe = contact.name.replace(/[^a-zA-Z0-9-_]/g, "_");
    doc.save(`ekstre_${safe}_${start}_${end}.pdf`);
    toast.success("PDF indirildi");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="contact-statement-modal"
        className="bg-white border border-black w-full max-w-5xl my-8"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-100 border border-black/10 md:hidden"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="label-caps">Cari Ekstre</div>
              <h2 className="font-display text-2xl font-bold">{contact.name}</h2>
              <div className="text-xs text-neutral-500 mt-0.5">
                {TYPES[contact.type]}
                {contact.tax_id && ` · VKN: ${contact.tax_id}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="statement-export-excel"
              onClick={exportExcel}
              disabled={!data}
              className="brutal-btn bg-white border border-black px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
            >
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button
              data-testid="statement-export-pdf"
              onClick={exportPDF}
              disabled={!data}
              className="brutal-btn bg-black text-white px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
            >
              <Download size={14} /> PDF
            </button>
            <button onClick={onClose} className="p-2 hover:bg-neutral-100"><X size={18} /></button>
          </div>
        </div>

        {/* Date range */}
        <div className="p-5 border-b border-black/10 flex flex-wrap items-end gap-3 bg-neutral-50">
          <label className="text-sm">
            <div className="label-caps mb-1">Başlangıç</div>
            <input
              type="date"
              data-testid="statement-start"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="border border-black/20 px-3 py-1.5 bg-white outline-none"
            />
          </label>
          <label className="text-sm">
            <div className="label-caps mb-1">Bitiş</div>
            <input
              type="date"
              data-testid="statement-end"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="border border-black/20 px-3 py-1.5 bg-white outline-none"
            />
          </label>
          <button
            data-testid="statement-apply"
            onClick={load}
            className="brutal-btn bg-black text-white px-4 py-1.5 text-sm"
          >
            Uygula
          </button>
          <div className="ml-auto flex gap-2 text-xs">
            {[
              ["Bu Ay", () => {
                const d = new Date();
                setStart(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
                setEnd(todayISO());
              }],
              ["Son 3 Ay", () => {
                const d = new Date();
                setStart(new Date(d.getFullYear(), d.getMonth() - 2, 1).toISOString().slice(0, 10));
                setEnd(todayISO());
              }],
              ["Bu Yıl", () => {
                const d = new Date();
                setStart(new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10));
                setEnd(todayISO());
              }],
            ].map(([l, fn]) => (
              <button
                key={l}
                onClick={fn}
                className="border border-black/20 px-2 py-1 uppercase tracking-widest hover:border-black"
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Summary cards */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 border-b border-black/10">
            <StmtStat label="Dönem Başı" value={data.opening_balance} tid="stmt-opening" />
            <StmtStat label="Toplam Borç" value={data.total_income} tone="rose" tid="stmt-income" />
            <StmtStat label="Toplam Alacak" value={data.total_expense} tone="emerald" tid="stmt-expense" />
            <StmtStat label="Dönem Sonu" value={data.closing_balance}
                      tone={data.closing_balance > 0 ? "emerald" : data.closing_balance < 0 ? "rose" : ""}
                      tid="stmt-closing" bold />
          </div>
        )}

        {/* Ortalama Vade — açık faturalar üzerinden */}
        {data && (data.avg_maturity_receivable_days !== null || data.avg_maturity_payable_days !== null) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-5 border-b border-black/10 bg-neutral-50/60">
            <MaturityCard
              label="Borç — Ortalama Vade"
              days={data.avg_maturity_receivable_days}
              date={data.avg_maturity_receivable_date}
              amount={data.avg_maturity_receivable_amount}
              count={data.avg_maturity_receivable_count}
              refDate={data.reference_date}
              tone="rose"
              tid="stmt-avg-borc"
              emptyHint="Açık satış faturası yok"
            />
            <MaturityCard
              label="Alacak — Ortalama Vade"
              days={data.avg_maturity_payable_days}
              date={data.avg_maturity_payable_date}
              amount={data.avg_maturity_payable_amount}
              count={data.avg_maturity_payable_count}
              refDate={data.reference_date}
              tone="emerald"
              tid="stmt-avg-alacak"
              emptyHint="Açık alış faturası yok"
            />
          </div>
        )}

        {/* Ledger */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="statement-table">
            <thead>
              <tr className="border-b-2 border-black/80 bg-neutral-50">
                <th className="text-left px-4 py-3 label-caps">Tarih</th>
                <th className="text-left px-4 py-3 label-caps">Açıklama</th>
                <th className="text-left px-4 py-3 label-caps">Kategori</th>
                <th className="text-right px-4 py-3 label-caps">Borç (+)</th>
                <th className="text-right px-4 py-3 label-caps">Alacak (−)</th>
                <th className="text-right px-4 py-3 label-caps">Yürüyen Bakiye</th>
              </tr>
            </thead>
            <tbody>
              {data && (
                <tr className="border-b border-black/10 bg-neutral-50/40">
                  <td colSpan={5} className="px-4 py-3 text-neutral-600 uppercase text-xs tracking-widest">Dönem Başı Bakiye</td>
                  <td className={`px-4 py-3 text-right font-mono-num font-semibold ${data.opening_balance > 0 ? "text-emerald-700" : data.opening_balance < 0 ? "text-rose-600" : ""}`}>
                    {fmtTRY(data.opening_balance)}
                  </td>
                </tr>
              )}
              {loading && (
                <tr><td colSpan={6} className="text-center py-10 text-neutral-500 label-caps">Yükleniyor…</td></tr>
              )}
              {!loading && data?.transactions?.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-neutral-500">Bu dönem için hareket yok.</td></tr>
              )}
              {!loading && data?.transactions?.map((t) => {
                const isInv = t.source === "invoice";
                const isChq = t.source === "cheque";
                const clickable = isInv;
                const bgClass = isInv ? "bg-amber-50/40 cursor-pointer" : isChq ? "bg-sky-50/40" : "";
                return (
                  <tr
                    key={t.id}
                    data-testid={isInv ? `stmt-invoice-row-${t.id}` : (isChq ? `stmt-cheque-row-${t.id}` : undefined)}
                    onClick={clickable ? () => {
                      onClose();
                      navigate(`/faturalar?contact=${contact.id}&hl=${t.id}`);
                    } : undefined}
                    className={`border-b border-black/5 hover:bg-neutral-50 ${bgClass}`}
                    title={isInv ? "Faturaya git" : (isChq ? "Çek — Portföydeki durumu" : undefined)}
                  >
                    <td className="px-4 py-3 font-mono-num">{fmtDate(t.date)}
                      {t.due_date && <div className="text-[10px] text-neutral-400">Vade: {fmtDate(t.due_date)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {isInv && (
                        <span className="inline-block border border-amber-400 bg-amber-100 text-amber-800 text-[9px] uppercase tracking-widest px-1.5 py-0.5 mr-2 align-middle">
                          Fatura ↗
                        </span>
                      )}
                      {isChq && (
                        <span className="inline-block border border-sky-400 bg-sky-100 text-sky-800 text-[9px] uppercase tracking-widest px-1.5 py-0.5 mr-2 align-middle">
                          Çek
                        </span>
                      )}
                      {t.description || "—"}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{t.category || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-rose-600">
                      {t.income ? fmtTRY(t.income) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num text-emerald-700">
                      {t.expense ? fmtTRY(t.expense) : "—"}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono-num font-semibold ${t.balance > 0 ? "text-emerald-700" : t.balance < 0 ? "text-rose-600" : ""}`}>
                      {fmtTRY(t.balance)}
                    </td>
                  </tr>
                );
              })}
              {data && (
                <tr className="border-t-2 border-black/80 bg-neutral-50">
                  <td colSpan={3} className="px-4 py-3 uppercase text-xs tracking-widest font-medium">Dönem Sonu</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-emerald-700">
                    {fmtTRY(data.total_income)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-rose-600">
                    {fmtTRY(data.total_expense)}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono-num font-bold text-base ${data.closing_balance > 0 ? "text-emerald-700" : data.closing_balance < 0 ? "text-rose-600" : ""}`}>
                    {fmtTRY(data.closing_balance)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StmtStat({ label, value, tone, tid, bold }) {
  const color = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "";
  return (
    <div className={`border border-black/10 p-4 ${bold ? "bg-black text-white border-black" : "bg-white"}`} data-testid={tid}>
      <div className={`label-caps ${bold ? "text-white/60" : ""}`}>{label}</div>
      <div className={`font-mono-num text-xl font-semibold mt-1 ${bold ? "text-white" : color}`}>{fmtTRY(value)}</div>
    </div>
  );
}

function MaturityCard({ label, days, date, amount, count, refDate, tone, tid, emptyHint }) {
  const color = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "";
  const empty = days === null || days === undefined;
  return (
    <div data-testid={tid} className="border border-black/10 bg-white p-4">
      <div className="label-caps">{label}</div>
      {empty ? (
        <div className="mt-2 text-sm text-neutral-400">{emptyHint || "Açık fatura yok"}</div>
      ) : (
        <div className="mt-2 flex items-baseline gap-3">
          <span className={`font-mono-num text-3xl font-bold ${color}`}>{Math.round(days)} <span className="text-sm font-normal text-neutral-500">gün</span></span>
          <span className="font-mono-num text-sm text-neutral-500">→ {fmtDate(date)}</span>
        </div>
      )}
      {!empty && (
        <div className="text-[11px] text-neutral-500 mt-2">
          {count} açık fatura · {fmtTRY(amount)} · Ref: {fmtDate(refDate)}
        </div>
      )}
    </div>
  );
}
